import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import KanbanView from './components/KanbanView';
import LeadsForm from './components/LeadsForm';
import BulkActionsDropdown from './components/BulkActionsDropdown';
import { AppSelect } from '../../components/common/select';
// import OverviewView from './components/OverviewView';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { getActivePeriodRange, getActiveFilterCount } from '../../components/common/AdvancedFilter/periodUtils';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import Alert from '../../components/Alert/Alert';
import { useLeadsForm } from './hooks/useLeadsForm';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import api from '../../api';
import { useAuth } from '../../contexts/AuthProvider';
import { ActivityLogger } from '../../utils/activityLogger';
import * as XLSX from 'xlsx';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';


const DEFAULT_PAGE_SIZE = 10;

const getEmptyLeadsFilters = () => ({
  period: '',
  startDate: '',
  endDate: '',
  createdBy: [],
  assignedTo: [],
  department: [],
  source: [],
  service: [],
  sector: '',
  standard: '',
  branchId: [],
  taggingIds: [],
  stage: '',
  proposalConverted: '',
  leadUpdateFrom: '',
  leadUpdateTo: '',
  taskUpdateFrom: '',
  taskUpdateTo: '',
});

// Map frontend column keys to backend sort property names (Spring sort param) — all backend-based
const LEADS_SORT_KEY_TO_BACKEND = {
  projectName: 'projectName',
  companyName: 'companyName',
  source: 'source',
  stage: 'stage',
  pic: 'pic',
  email: 'email',
  sector: 'sector',
  createdBy: 'createdBy',
  department: 'department',
  assignedTo: 'assignedTo',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  tagging: 'tagging',
  partnerType: 'partnerType',
  proposalStatus: 'hasProposal',
  taskUpdateDate: 'taskUpdateDate',
  taskUpdateBy: 'taskUpdateBy',
};

const Leads = () => {
  const navigate = useNavigate();
  const { loadPersistedState, persistState } = usePersistedFilters('leads');
  const [leadData, setLeadData] = useState([]);
  // const [leadData2, setLeadData2] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [kanbanLoading, setKanbanLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('list-data');

  // Server-side pagination (backend returns Page<T>)
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Server-side sort (ascending/descending handled by backend)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);

  // Bulk actions state
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [showBulkReassignModal, setShowBulkReassignModal] = useState(false);
  const [bulkReassignUserId, setBulkReassignUserId] = useState('');
  const [isBulkReassigning, setIsBulkReassigning] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Alert state
  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });

  // Use global auth context with capabilities
  const { user, can, capabilitiesLoading } = useAuth();
  
  // Helper function to check leads-specific capabilities
  const canLeads = (capabilityKey) => can(capabilityKey, 'leads');
  
  // ✅ TASK UPDATE PERMISSION: Check if user can view task update date
  const canViewTaskUpdate = can('canviewtaskupdate', 'task');

  const formatDateDisplay = (dateValue) => {
    if (!dateValue) return '';
  
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
  
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
  
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
  
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const {
    formData,
    formErrors,
    serviceSections,
    uploadedFiles,
    fetchStandardsByServiceId,
    handleInputChange,
    handleTextChange,
    handleFormSubmit,
    addServiceSection,
    deleteServiceSection,
    onDrop,
    removeFile,
    resetForm,
    options,
    fetchContactOptions,
    standardsOptionsMap,
    initializeServiceSections,
    isIsccService,
    formCascadeUsers,
    formCascadeDepts
  } = useLeadsForm();

  const [searchQuery, setSearchQuery] = useState('');

  const [taggingOptions, setTaggingOptions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [allUsersWithDept, setAllUsersWithDept] = useState([]);
  const [filterData, setFilterData] = useState(getEmptyLeadsFilters);
  const [appliedFilters, setAppliedFilters] = useState(getEmptyLeadsFilters);

  const [filterOptions, setFilterOptions] = useState({
    createdByUsers: [],
    assignedToUsers: [],
    stages: [],
    sectors: [],
    sources: [],
    services: [],
    departments: [],
    standards: []
  });
  
  // ✅ PERFORMANCE: Track if filter options have been loaded
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Gabungkan options dari API dengan additional options
  const allOptions = {
    ...options // options dari API (companyOptions, contactOptions) akan menimpa jika ada
  };


  const handleFilterChange = (name, value) => {
    if (name === 'createdBy' || name === 'assignedTo' || name === 'department' || name === 'source' || name === 'service' || name === 'branchId' || name === 'taggingIds') {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      setFilterData(prev => ({ ...prev, [name]: arr }));
      return;
    }
    setFilterData(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterReset = () => {
    const empty = getEmptyLeadsFilters();
    setFilterData(empty);
    setAppliedFilters(empty);
    setSearchQuery('');
    setPage(0);
    persistState({ filters: empty, search: '', page: 0, size });
    fetchLeads({}, 0, size, sortConfig);
  };

  const handleFilterApply = () => {
    const { startDate, endDate } = getActivePeriodRange(filterData);
    const applied = { ...filterData, startDate, endDate };
    setAppliedFilters(applied);
    setPage(0);
    persistState({ filters: applied, search: searchQuery, page: 0, size });
    fetchLeads(applied, 0, size, sortConfig);
  };

  const handleOpenFilter = () => {
    fetchFilterOptions();
  };

  const getClearedLeadsFilters = (filters, key) => {
    const keysToClear = ['period', 'startDate', 'endDate'].includes(key)
      ? ['period', 'startDate', 'endDate']
      : key === 'leadUpdateFrom' || key === 'leadUpdateTo'
        ? ['leadUpdateFrom', 'leadUpdateTo']
        : key === 'taskUpdateFrom' || key === 'taskUpdateTo'
          ? ['taskUpdateFrom', 'taskUpdateTo']
          : [key];
    const nextFilters = { ...filters };
    keysToClear.forEach((fieldKey) => {
      nextFilters[fieldKey] = ['createdBy', 'assignedTo', 'department', 'source', 'service', 'branchId', 'taggingIds'].includes(fieldKey) ? [] : '';
    });
    return nextFilters;
  };

  const handleLeadsChipRemove = (key) => {
    if (!key) return;
    const keysToCheck = ['period', 'startDate', 'endDate'].includes(key)
      ? ['period', 'startDate', 'endDate']
      : key === 'leadUpdateFrom' || key === 'leadUpdateTo'
        ? ['leadUpdateFrom', 'leadUpdateTo']
        : key === 'taskUpdateFrom' || key === 'taskUpdateTo'
          ? ['taskUpdateFrom', 'taskUpdateTo']
          : [key];
    const hasActiveValue = keysToCheck.some((fieldKey) => {
      const value = appliedFilters[fieldKey];
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });
    if (!hasActiveValue) return;

    const nextAppliedFilters = getClearedLeadsFilters(appliedFilters, key);
    setFilterData(prev => getClearedLeadsFilters(prev, key));
    setAppliedFilters(nextAppliedFilters);
    setPage(0);
    persistState({ filters: nextAppliedFilters, search: searchQuery, page: 0, size });
    fetchLeads(nextAppliedFilters, 0, size, sortConfig);
  };

  // Handle tab change with loading states
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);

    // Show loading for the specific tab
    if (tabName === 'overview') {
      setOverviewLoading(true);
      setTimeout(() => setOverviewLoading(false), 500); // Simulate loading
    } else if (tabName === 'list-data') {
      setTableLoading(true);
      setTimeout(() => setTableLoading(false), 500); // Simulate loading
    } else if (tabName === 'kanban') {
      setKanbanLoading(true);
      setTimeout(() => setKanbanLoading(false), 500); // Simulate loading
    }
  };

  // ✅ Build the same query params/URL used by fetchLeads — shared so export-all can reuse the
  // identical filter/permission logic without duplicating it and drifting out of sync.
  const buildLeadsQueryUrl = useCallback((filters = {}, pageNum, pageSize, sortConfigOverride = null) => {
    const params = new URLSearchParams();
    params.set('page', String(pageNum));
    params.set('size', String(pageSize));

      // Map period/startDate/endDate → dateFrom/dateTo for backend
      if (filters.startDate) params.append('dateFrom', filters.startDate);
      if (filters.endDate) params.append('dateTo', filters.endDate);

      const arrayKeys = ['createdBy', 'assignedTo', 'department', 'source', 'service', 'branchId', 'taggingIds'];
      const scalarKeys = ['sector', 'stage', 'leadUpdateFrom', 'leadUpdateTo', 'taskUpdateFrom', 'taskUpdateTo', 'search'];
      const skipKeys = new Set(['period', 'startDate', 'endDate', 'dateFrom', 'dateTo', 'proposalConverted', 'standard', ...arrayKeys, ...scalarKeys]);

      // Standard filter → backend expects "standards" (plural), not "standard"
      if (filters.standard != null && filters.standard !== '') {
        params.append('standards', filters.standard);
      }

      // Converted to Proposal filter → hasProposal param
      if (filters.proposalConverted === 'converted') params.append('hasProposal', 'true');
      else if (filters.proposalConverted === 'not_converted') params.append('hasProposal', 'false');

      // Handle array fields → comma-separated
      arrayKeys.forEach(key => {
        const val = filters[key];
        if (Array.isArray(val) && val.length > 0) {
          params.append(key, val.join(','));
        } else if (typeof val === 'string' && val) {
          params.append(key, val);
        }
      });

      // Handle scalar fields
      scalarKeys.forEach(key => {
        const val = filters[key];
        if (val != null && val !== '') params.append(key, val);
      });

      // Handle any remaining keys not explicitly handled
      Object.keys(filters).forEach(key => {
        if (skipKeys.has(key)) return;
        const rawVal = filters[key];
        if (rawVal != null && rawVal !== '') {
          const normalized = typeof rawVal === 'object' && 'value' in rawVal ? rawVal.value : rawVal;
          params.append(key, normalized);
        }
      });
      
      // ✅ Permission-based filtering hierarchy:
      // 1. view_global → see all (no filter)
      // 2. view_department → filter by user's department
      // 3. view_own → filter by user ID (created by OR assigned to)
      // NOTE: Don't override if user explicitly selects createdBy or assignedTo filter
      
      if (!canLeads('view_global')) {
        if (canLeads('view_department')) {
          // Filter by department (unless user explicitly filters by createdBy/assignedTo)
          if (!params.has('createdBy') && !params.has('assignedTo') && user?.departmentId) {
            params.append('viewDepartmentId', user.departmentId);
          }
        } else if (canLeads('view_own')) {
          // Filter by user (created by OR assigned to)
          if (!params.has('createdBy') && !params.has('assignedTo') && user?.id) {
            params.append('viewOwnUserId', user.id);
          }
        }
      }

      // ✅ Server-side sort: backend expects sort=propertyName,direction (e.g. sort=companyName,asc)
      const effectiveSort = sortConfigOverride ?? sortConfig;
      if (effectiveSort?.key && LEADS_SORT_KEY_TO_BACKEND[effectiveSort.key]) {
        const backendSortProperty = LEADS_SORT_KEY_TO_BACKEND[effectiveSort.key];
        params.set('sort', `${backendSortProperty},${effectiveSort.direction}`);
      }

      // Use filter endpoint if filters are applied, otherwise use regular endpoint
      // Note: Both endpoints now support viewOwnUserId parameter for permission filtering and return Page<T>
      const hasFilters = [...params.keys()].some(k => k !== 'page' && k !== 'size' && k !== 'sort');
      return hasFilters ? `/crm/leads/filter?${params.toString()}` : `/crm/leads?${params.toString()}`;
  }, [canLeads, user, sortConfig]);

  // ✅ PERFORMANCE: Memoize fetchLeads to prevent recreation on every render
  const fetchLeads = useCallback(async (filters = {}, pageNum = page, pageSize = size, sortConfigOverride = null) => {
    try {
      // ✅ OPTIMIZATION: Only set loading for active tab
      if (activeTab === 'list-data') {
        setTableLoading(true);
      } else if (activeTab === 'kanban') {
        setKanbanLoading(true);
      } else {
        setOverviewLoading(true);
      }

      const url = buildLeadsQueryUrl(filters, pageNum, pageSize, sortConfigOverride);
      const response = await api.get(url);
      const data = response.data;
      const list = Array.isArray(data) ? data : (data?.content ?? []);
      setLeadData(list);
      if (data && typeof data.totalElements === 'number') {
        setTotalElements(data.totalElements);
        setTotalPages(data.totalPages ?? Math.max(1, Math.ceil(data.totalElements / pageSize)));
      } else {
        setTotalElements(list.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Fetch leads error:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: error?.config?.url,
      });
      showAlert('danger', 'Failed to fetch leads data. Please refresh the page.');
    } finally {
      setTableLoading(false);
      setOverviewLoading(false);
      setKanbanLoading(false);
    }
  }, [activeTab, buildLeadsQueryUrl, page, size]);

  // Refresh function to reload leads data (current page)
  const refreshLeadsData = async () => {
    await fetchLeads(appliedFilters, page, size, sortConfig);
  };

  // Stats data for overview
  const stats = {
    leads: {
      total: 56,
      new: 40,
      existing: 16
    },
    hot: {
      total: 37,
      new: 20,
      existing: 17
    },
    negotiation: {
      total: 12,
      new: 10,
      existing: 2
    },
    converted: {
      total: 56,
      new: 40,
      existing: 16
    }
  };

  const handleEditLead = async (lead) => {
    if (!canLeads('edit')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to edit leads.' });
      return;
    }
    
    setIsEditMode(true);
    // Reset form
    resetForm();
    
    try {
      const response = await api.get(`/crm/leads/${lead.id}`);
      setEditingLead(response.data);
      // Open modal after full detail (including services) is loaded
      setIsModalOpen(true);
    } catch (error) {
      console.error('Failed to load lead details:', error);
      setIsEditMode(false);
      setEditingLead(null);
      showAlert('danger', 'Failed to load lead details. Please try again.');
    }
  };

  const handleDeleteLead = async (lead) => {
    if (!canLeads('delete')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to delete leads.' });
      return;
    }
    
    // Set the lead to delete and open confirmation modal
    setLeadToDelete(lead);
    setDeleteModalOpen(true);
  };

  const confirmDeleteLead = async () => {
    if (!leadToDelete) return;

    try {
      // Asumsikan lead.id adalah ID yang benar
      await api.delete(`/crm/leads/${leadToDelete.id}`);
      
      // Log delete activity
      await ActivityLogger.logLeadActivity('DELETE_LEAD', leadToDelete);

      // Setelah berhasil, hapus lead dari state
      setLeadData(prev => prev.filter(l => l.id !== leadToDelete.id));

      // Close modal and reset state
      setDeleteModalOpen(false);
      setLeadToDelete(null);

      // Show success message using the project's Alert component
      // Note: Using a simple alert for now, but you can replace this with your Alert component
      showAlert('success', 'Lead deleted successfully!');
    } catch (error) {
      showAlert('danger', 'Failed to delete lead.');
    }
  };

  const cancelDeleteLead = () => {
    setDeleteModalOpen(false);
    setLeadToDelete(null);
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    // Auto-hide alert after 5 seconds
    setTimeout(() => {
      setAlert({ show: false, type: 'success', message: '' });
    }, 5000);
  };

  const hideAlert = () => {
    setAlert({ show: false, type: 'success', message: '' });
  };

  // Bulk actions
  const handleOpenBulkReassign = () => {
    if (!canLeads('edit')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to reassign leads.' });
      return;
    }
    setBulkReassignUserId('');
    setShowBulkReassignModal(true);
  };

  const cancelBulkReassign = () => {
    setShowBulkReassignModal(false);
    setBulkReassignUserId('');
  };

  const confirmBulkReassign = async () => {
    if (!bulkReassignUserId || selectedLeadIds.length === 0) return;

    setIsBulkReassigning(true);
    try {
      await api.patch('/crm/leads/bulk/assignee', {
        leadIds: selectedLeadIds,
        assignedUserId: bulkReassignUserId,
      });

      setShowBulkReassignModal(false);
      setSelectedLeadIds([]);
      showAlert('success', `${selectedLeadIds.length} lead(s) reassigned successfully!`);
      await fetchLeads(appliedFilters, page, size, sortConfig);
    } catch (error) {
      showAlert('danger', 'Failed to reassign leads.');
    } finally {
      setIsBulkReassigning(false);
    }
  };

  const handleOpenBulkDelete = () => {
    if (!canLeads('delete')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to delete leads.' });
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const cancelBulkDelete = () => {
    setShowBulkDeleteModal(false);
  };

  const confirmBulkDelete = async () => {
    if (selectedLeadIds.length === 0) return;

    setIsBulkDeleting(true);
    try {
      await api.delete('/crm/leads/bulk', { data: { leadIds: selectedLeadIds } });

      setShowBulkDeleteModal(false);
      showAlert('success', `${selectedLeadIds.length} lead(s) deleted successfully!`);
      setSelectedLeadIds([]);
      await fetchLeads(appliedFilters, page, size, sortConfig);
    } catch (error) {
      showAlert('danger', 'Failed to delete leads.');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Wrapper function to manage isSubmitting state
  const handleFormSubmitWithLoading = async (e, onSuccess, editingLead) => {
    setIsSubmitting(true);
    try {
      await handleFormSubmit(e, onSuccess, editingLead);
    } catch (error) {
      console.error('Error submitting form:', error);
      showAlert('danger', 'Failed to submit lead. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportExcel = async () => {
    if (!canLeads('export_data')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to export data.' });
      return;
    }

    try {
      // Fetch ALL matching leads (not just the current page) using the same filters/search/sort
      // applied on screen, then fall back to the currently loaded page if the export fetch fails.
      let dataToExport = filteredLeads;
      try {
        const searchFilters = searchQuery ? { ...appliedFilters, search: searchQuery } : appliedFilters;
        const exportUrl = buildLeadsQueryUrl(searchFilters, 0, totalElements > 0 ? totalElements : 10000, sortConfig);
        const exportResponse = await api.get(exportUrl);
        const exportData = exportResponse.data;
        const exportList = Array.isArray(exportData) ? exportData : (exportData?.content ?? []);
        if (exportList.length > 0) {
          dataToExport = exportList;
        }
      } catch (fetchError) {
        console.error('Failed to fetch all leads for export, falling back to current page:', fetchError);
      }

      // Prepare data for Excel export
      const excelData = dataToExport.map(row => {
        const baseData = {
          'Project Name': row.projectName || '',
          'Company Name': row.companyName || '',
          'Lead Source': row.sourceName || row.leadSource || row.source?.name || row.source || '-',
          'Stage': row.stage || '',
          'PIC': row.pic || '',
          'Partner Type': row.partnerType || '',
          'Email': row.email || '',
          'Created By': `${row.createdBy?.firstName || ''} ${row.createdBy?.lastName || ''}`.trim() || '-',
          'Department': row.department?.name || 'N/A',
          'Assigned To': `${row.assignedTo?.firstName || ''} ${row.assignedTo?.lastName || ''}`.trim() || '-',
          'Created At': formatDateDisplay(row.createdAt),
          'Updated At': formatDateDisplay(row.updatedAt) || '-'
        };
        
        // ✅ TASK UPDATE PERMISSION: Only include Task Update if user has permission
        if (canViewTaskUpdate) {
          baseData['Task Update'] = formatDateDisplay(row.taskUpdateDate) || '-';
          baseData['Task Update By'] = row.taskUpdateBy ? `${row.taskUpdateBy.firstName || ''} ${row.taskUpdateBy.lastName || ''}`.trim() : '-';
        }
        
        return baseData;
      });

      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 30 }, // Project Name
        { wch: 30 }, // Company Name
        { wch: 20 }, // Lead Source
        { wch: 15 }, // Stage
        { wch: 25 }, // PIC
        { wch: 20 }, // Partner Type
        { wch: 30 }, // Email
        { wch: 20 }, // Created By
        { wch: 20 }, // Department
        { wch: 20 }, // Assigned To
        { wch: 15 }, // Created At
        { wch: 15 }, // Updated At
        ...(canViewTaskUpdate ? [{ wch: 15 }, { wch: 20 }] : [])  // Task Update, Task Update By (only if permission)
      ];
      worksheet['!cols'] = columnWidths;

      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');

      // Generate file name with timestamp
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `leads_export_${timestamp}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, fileName);
      
      showAlert('success', 'Leads exported to Excel successfully!');
    } catch (e) {
      console.error('Export Excel failed:', e);
      showAlert('danger', 'Failed to export leads to Excel. Please try again.');
    }
  };

  const handleExportPDF = () => {
    if (!canLeads('export_data')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to export data.' });
      return;
    }
    
    try {
      const win = window.open('', '_blank');
      if (!win) return;
      
      const style = `
        <style>
          body { font-family: Arial, sans-serif; padding: 16px; }
          h2 { margin: 0 0 12px 0; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 12px; }
          th { background: #f5f5f5; }
        </style>
      `;
      
      const header = ['Project Name','Company Name','Lead Source','Stage','PIC','Partner Type','Email','Created By','Department','Assigned To','Created At','Updated At'];
      // ✅ TASK UPDATE PERMISSION: Add Task Update headers only if user has permission
      if (canViewTaskUpdate) {
        header.push('Task Update');
        header.push('Task Update By');
      }
      
      const rows = filteredLeads.map(r => {
        const baseRow = [
          r.projectName || '',
          r.companyName || '',
          r.sourceName || r.leadSource || r.source?.name || r.source || '-',
          r.stage || '',
          r.pic || '',
          r.partnerType || '',
          r.email || '',
          `${r.createdBy?.firstName || ''} ${r.createdBy?.lastName || ''}`.trim() || '-',
          r.department?.name || 'N/A',
          `${r.assignedTo?.firstName || ''} ${r.assignedTo?.lastName || ''}`.trim() || '-',
          formatDateDisplay(r.createdAt),
          formatDateDisplay(r.updatedAt) || '-'
        ];
        
        // ✅ TASK UPDATE PERMISSION: Add Task Update data only if user has permission
        if (canViewTaskUpdate) {
          baseRow.push(formatDateDisplay(r.taskUpdateDate) || '-');
          baseRow.push(r.taskUpdateBy ? `${r.taskUpdateBy.firstName || ''} ${r.taskUpdateBy.lastName || ''}`.trim() : '-');
        }
        
        return baseRow;
      });
      
      const table = `
        <table>
          <thead>
            <tr>${header.map(h=>`<th>${h}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${rows.map(cols => `<tr>${cols.map(c => `<td>${c != null ? String(c) : ''}</td>`).join('')}</tr>`).join('')}
          </tbody>
        </table>
      `;
      
      win.document.write(`<!doctype html><html><head><meta charset="utf-8">${style}<title>Leads Export</title></head><body><h2>Leads Export</h2>${table}</body></html>`);
      win.document.close();
      win.focus();
      win.print();
      
      showAlert('success', 'Leads exported to PDF successfully!');
    } catch (e) {
      showAlert('danger', 'Failed to export leads to PDF. Please try again.');
    }
  };

  const handleAddNew = (prospect) => {
    if (!canLeads('create')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to create leads.' });
      return;
    }
    
    setIsEditMode(false);
    setEditingLead(null);

    // Reset form first
    resetForm();

    // Use setTimeout to ensure resetForm completes before setting new data
    setTimeout(() => {
      if (prospect && !prospect.preventDefault) {  // Check that prospect exists and is not an event
        // Find the correct ID field - try different possible field names
        const companyId = prospect.id || prospect.companyId || prospect._id || prospect.ID;
        // Set company ID as string to match dropdown value format
        if (companyId) {
          handleInputChange('company', String(companyId));
          handleInputChange('companyWebsite', prospect.website || '');
          handleInputChange('companyPhoneNumber', prospect.contactNumber || prospect.phone || '');
          handleInputChange('address', prospect.address || '');
          handleInputChange('sector', prospect.partnerType || prospect.sectorName || '');
          handleInputChange('contact', prospect.pic || prospect.contactName || '');
          handleInputChange('contactPhoneNumber', prospect.contactNumber || prospect.phone || '');
          handleInputChange('email', prospect.email || '');
          handleInputChange('stage', 'New');
          handleInputChange('projectName', '');
          handleInputChange('projectDescription', '');
          handleInputChange('leadSource', prospect.leadSource || '');

          // Fetch contacts for the selected company
          fetchContactOptions(String(companyId));
        } else {
          // Try to find company by name in company options
          const companyByName = options.companyOptions?.find(c =>
            c.label === prospect.companyName || c.name === prospect.companyName
          );
          if (companyByName) {
            handleInputChange('company', String(companyByName.value));
            handleInputChange('companyWebsite', prospect.website || '');
            handleInputChange('companyPhoneNumber', prospect.contactNumber || prospect.phone || '');
            handleInputChange('address', prospect.address || '');
            handleInputChange('sector', prospect.partnerType || prospect.sectorName || '');
            handleInputChange('contact', prospect.pic || prospect.contactName || '');
            handleInputChange('email', prospect.email || '');
            handleInputChange('stage', 'New');
            handleInputChange('projectName', '');
            handleInputChange('projectDescription', '');
            handleInputChange('leadSource', prospect.leadSource || '');

            fetchContactOptions(String(companyByName.value));
          }
        }
      }

      // Open modal after data is set
      setIsModalOpen(true);
    }, 100);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const getProposalStatusBadge = (status) => {
    const colors = {
      'Converted': 'success',
      'Not Converted': 'orange'
    };
    return `badge badge-outline text-${colors[status] || 'secondary'}`;
  };

  // ✅ PERFORMANCE: Memoize click handlers to prevent recreation on every render
  const handleProjectClick = useCallback(async (lead) => {
    // Log view activity
    await ActivityLogger.logLeadActivity('VIEW_LEAD_DETAIL', lead);
    
    // Navigate to detail page
    navigate(`/leads/detail/${lead.id}`);
  }, [navigate]);

  const handleCompanyClick = useCallback((companyId) => {
    navigate(`/clients/${companyId}`);
  }, [navigate]);

  // ✅ PERFORMANCE: Memoize columns to prevent recreation on every render
  // Only recreate when canViewTaskUpdate changes (not on every state update)
  const columns = useMemo(() => [
    {
      key: 'projectName',
      label: 'Project Name',
      sortable: true,
      className: 'text-truncate',
      render: (value, item) => (
        <a
          href={`/leads/detail/${item.id}`}
          className="text-primary text-truncate d-inline-block text-decoration-none"
          style={{ 
            cursor: 'pointer',
            maxWidth: '150px',
            width: '150px'
          }}
          onClick={(e) => {
            // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return; // Let browser handle it
            }
            e.preventDefault();
            handleProjectClick(item);
          }}
          onAuxClick={(e) => {
            // Handle middle mouse button click
            if (e.button === 1) {
              return; // Let browser handle it
            }
          }}
          title={value}
        >
          {value}
        </a>
      )
    },
    {
      key: 'companyName',
      label: 'Company Name',
      sortable: true,
      className: 'text-truncate',
      render: (value, item) => (
        <a
          href={`/clients/${item.companyId}`}
          className="text-primary text-truncate d-inline-block text-decoration-none"
          style={{
            cursor: 'pointer',
            maxWidth: '200px',
            width: '200px'
          }}
          onClick={(e) => {
            // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return; // Let browser handle it
            }
            e.preventDefault();
            handleCompanyClick(item.companyId);
          }}
          onAuxClick={(e) => {
            // Handle middle mouse button click
            if (e.button === 1) {
              return; // Let browser handle it
            }
          }}
          title={value}
          aria-label={`View client details for ${value}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCompanyClick(item.companyId);
            }
          }}
        >
          {value}
        </a>
      )
    },
    {
      key: 'tagging',
      label: 'Tagging',
      sortable: true,
      className: 'text-truncate',
      render: (value, item) => {
        const raw = item.tagging
          ?? (item.taggings && Array.isArray(item.taggings) ? item.taggings.map(t => t.name).join(', ') : null)
          ?? item.company?.tagging
          ?? (item.company?.taggings && Array.isArray(item.company.taggings) ? item.company.taggings.map(t => t.name).join(', ') : null)
          ?? '';
        const rawStr = typeof raw === 'string' ? raw : '';
        const tags = rawStr ? rawStr.split(',').map(s => s.trim()).filter(Boolean) : [];
        if (tags.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {tags.map((tag, i) => (
              <span
                key={i}
                className="badge bg-blue-lt text-wrap"
                title={tag}
                style={{ fontSize: '0.7rem' }}
              >
                {tag}
              </span>
            ))}
          </span>
        );
      }
    },
    {
      key: 'source',
      label: 'Lead Source',
      sortable: true,
      className: 'text-truncate',
      render: (value, item) => {
        const sourceValue = value || item.sourceName || item.leadSource || item.source?.name || item.source || '-';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '180px', width: '180px' }}
            title={sourceValue}
          >
            {sourceValue}
          </span>
        );
      }
    },
    { 
      key: 'sector', 
      label: 'Sector',
      sortable: true,
      className: 'text-truncate',
      render: (value, item) => {
        const sectorValue = value || item.sector?.name || '';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={sectorValue}
          >
            {sectorValue}
          </span>
        );
      }
    },
    {
      key: 'service',
      label: 'Service',
      sortable: false,
      className: 'text-truncate',
      render: (value, item) => {
        const services = (item.serviceNames || '').split(',').map(s => s.trim()).filter(Boolean);
        if (services.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {services.map((svc, i) => (
              <span
                key={i}
                className="badge bg-green-lt text-wrap"
                title={svc}
                style={{ fontSize: '0.7rem' }}
              >
                {svc}
              </span>
            ))}
          </span>
        );
      }
    },
    {
      key: 'standard',
      label: 'Standard',
      sortable: false,
      className: 'text-truncate',
      render: (value, item) => {
        const standards = (item.standardNames || '').split(',').map(s => s.trim()).filter(Boolean);
        if (standards.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {standards.map((std, i) => (
              <span
                key={i}
                className="badge bg-azure-lt text-wrap"
                title={std}
                style={{ fontSize: '0.7rem' }}
              >
                {std}
              </span>
            ))}
          </span>
        );
      }
    },
    {
      key: 'stage',
      label: 'Stage',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '150px', width: '150px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'pic', 
      label: 'PIC',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'partnerType', 
      label: 'Partner Type',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '150px', width: '150px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'email', 
      label: 'Email',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'createdBy', 
      label: 'Created By',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim(),
      render: (value, item) => {
        const fullName = `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim();
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '160px', width: '160px' }}
            title={fullName}
          >
            {fullName}
          </span>
        );
      }
    },
    {
      key: 'branch',
      label: 'Branch',
      sortable: false,
      className: 'text-truncate',
      render: (value, item) => {
        const branchName = item.branchName || 'N/A';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={branchName}
          >
            {branchName}
          </span>
        );
      }
    },
    {
      key: 'department',
      label: 'Department',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => item.department?.name || 'N/A',
      render: (value, item) => {
        const deptName = item.department?.name || 'N/A';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={deptName}
          >
            {deptName}
          </span>
        );
      }
    },
    { 
      key: 'assignedTo', 
      label: 'Assigned To',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => `${item.assignedTo?.firstName || ''} ${item.assignedTo?.lastName || ''}`.trim(),
      render: (value, item) => {
        const fullName = `${item.assignedTo?.firstName || ''} ${item.assignedTo?.lastName || ''}`.trim();
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '160px', width: '160px' }}
            title={fullName}
          >
            {fullName}
          </span>
        );
      }
    },
    {
      key: 'proposalStatus',
      label: 'Proposal Status',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => (item.hasProposal ? 1 : 0),
      render: (value, item) => {
        const converted = Boolean(item.hasProposal);
        const statusLabel = converted ? 'Converted' : 'Not Converted';
        return (
          <span
            className={`${getProposalStatusBadge(statusLabel)} text-truncate d-inline-block`}
            style={{ maxWidth: '120px', width: '120px' }}
            title={statusLabel}
          >
            {statusLabel}
          </span>
        );
      }
    },
    { 
      key: 'createdAt', 
      label: 'Created At', 
      type: 'date',
      sortable: true,
      className: 'text-truncate',
      render: (value) => {
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    { 
      key: 'updatedAt', 
      label: 'Updated At', 
      type: 'date',
      sortable: true,
      className: 'text-truncate',
      render: (value) => {
        if (!value) return '-';
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue || '-'}
          </span>
        );
      }
    },
    // ✅ TASK UPDATE PERMISSION: Only show column if user has permission
    ...(canViewTaskUpdate ? [
      {
        key: 'taskUpdateDate', 
        label: 'Task Update', 
        type: 'date',
        sortable: true,
        className: 'text-truncate',
        render: (value) => {
          if (!value || value === '-') return '-';
          const dateValue = formatDateDisplay(value);
          return (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: '150px', width: '150px' }}
              title={dateValue}
            >
              {dateValue || '-'}
            </span>
          );
        }
      },
      {
        key: 'taskUpdateBy', 
        label: 'Task Update By',
        sortable: true,
        className: 'text-truncate',
        sortValue: (item) => `${item.taskUpdateBy?.firstName || ''} ${item.taskUpdateBy?.lastName || ''}`.trim(),
        render: (value, item) => {
          const fullName = `${item.taskUpdateBy?.firstName || ''} ${item.taskUpdateBy?.lastName || ''}`.trim();
          if (!fullName) return '-';
          return (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: '160px', width: '160px' }}
              title={fullName}
            >
              {fullName}
            </span>
          );
        }
      }
    ] : [])
  ], [canViewTaskUpdate, navigate, handleProjectClick, handleCompanyClick]); // Only recreate when dependencies change

  // ✅ PERFORMANCE: Lazy load filter options only when needed
  const fetchFilterOptions = useCallback(async () => {
    // Skip if already loaded
    if (filterOptionsLoaded) return;
    
    try {
      const [filterOptionsRes, taggingRes, userBranchesRes, standardsRes, departmentsRes, sectorsRes, sourcesRes, stagesRes, internalUsersRes, servicesRes] = await Promise.all([
        api.get('/crm/leads/filter-options').catch(() => ({ data: {} })),
        api.get('/md/tagging', { params: { moduleKey: 'companies' } }).catch(() => ({ data: [] })),
        user?.id ? api.get(`/user-branches/user/${user.id}`).catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } }),
        api.get('/md/standards').catch(() => ({ data: [] })),
        api.get('/md/departement').catch(() => ({ data: [] })),
        api.get('/md/sector').catch(() => ({ data: [] })),
        api.get('/md/company-source').catch(() => ({ data: [] })),
        api.get('/md/lead-status').catch(() => ({ data: [] })),
        api.get('/md/internal-users/option-internal-user').catch(() => ({ data: [] })),
        api.get('/crm/services/options').catch(() => ({ data: [] }))
      ]);
      setTaggingOptions(taggingRes.data || []);
      setAllUsersWithDept(internalUsersRes.data || []);
      const branchData = userBranchesRes.data?.data ?? userBranchesRes.data ?? [];
      setBranches(Array.isArray(branchData) ? branchData : []);
      const filterOptionsData = filterOptionsRes.data || {};

      const toIdName = (item) => ({ id: item.id, name: item.name });

      // Transform createdByUsers from filter-options response
      const createdByUsers = (filterOptionsData.createdByUsers || []).map(user => ({
        id: user.id,
        value: user.id,
        name: user.name,
        label: user.name,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName
      }));
      
      // Transform assignedToUsers data
      const assignedToUsers = (filterOptionsData.assignedToUsers || []).map(user => ({
        id: user.id,
        value: user.id,
        name: user.name,
        label: user.name,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName
      }));

      setFilterOptions({
        createdByUsers,
        assignedToUsers,
        stages: (stagesRes.data || []).map(toIdName),
        sources: (sourcesRes.data || []).map(toIdName),
        sectors: (sectorsRes.data || []).map(toIdName),
        departments: (departmentsRes.data || []).map(toIdName),
        standards: (standardsRes.data || []).map(toIdName),
        services: (servicesRes.data || []).map(s => ({ id: s.id ?? s.value, name: s.name ?? s.label }))
      });
      
      setFilterOptionsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  }, [filterOptionsLoaded, user?.id]);

  // Fetch leads data (initial load and when page/size change from pagination)
  useEffect(() => {
    if (!capabilitiesLoading) {
      const saved = loadPersistedState();
      if (saved) {
        const restoredFilters = saved.filters ?? getEmptyLeadsFilters();
        const restoredSearch = saved.search ?? '';
        const restoredPage = saved.page ?? 0;
        const restoredSize = saved.size ?? DEFAULT_PAGE_SIZE;

        setAppliedFilters(restoredFilters);
        setFilterData(restoredFilters);
        setSearchQuery(restoredSearch);
        setPage(restoredPage);
        setSize(restoredSize);

        const filtersWithSearch = restoredSearch ? { ...restoredFilters, search: restoredSearch } : restoredFilters;
        fetchLeads(filtersWithSearch, restoredPage, restoredSize, sortConfig);
      } else {
        fetchLeads(appliedFilters, page, size, sortConfig);
      }
    }
  }, [capabilitiesLoading]);

  // Handle search submit (Enter): run search with current value, reset to page 0
  const handleSearchSubmit = useCallback((query) => {
    setSearchQuery(query);
    setPage(0);
    persistState({ filters: appliedFilters, search: query, page: 0, size });
    if (query) {
      fetchLeads({ ...appliedFilters, search: query }, 0, size, sortConfig);
    } else {
      fetchLeads(appliedFilters, 0, size, sortConfig);
    }
  }, [appliedFilters, fetchLeads, persistState, size, sortConfig]);

  // Tagging filter is now applied server-side via taggingIds param (see fetchLeads) —
  // leadData already reflects the applied tagging filter, no client-side re-filtering needed.
  const filteredLeads = leadData;

  // Build userId → { departmentId, status } lookup from internal-users endpoint
  const leadsUserDeptLookup = useMemo(() => {
    const map = new Map();
    (allUsersWithDept || []).forEach(u => {
      const deptId = u.departmentId || u.departementId;
      if (u.id != null) map.set(String(u.id), { deptId: deptId != null ? String(deptId) : null, status: u.status });
    });
    return map;
  }, [allUsersWithDept]);

  // Append " (Inactive)" to label for users with status "0"
  const withInactiveBadge = (userId, label) => {
    const entry = leadsUserDeptLookup.get(String(userId));
    return entry?.status === '0' ? `${label} (Inactive)` : label;
  };

  // Cascade: filter people options by selected department(s); mark inactive users
  const cascadedLeadsAssignedToOptions = useMemo(() => {
    const dept = filterData.department;
    const selectedDepts = Array.isArray(dept) ? dept.map(String).filter(Boolean) : dept ? [String(dept)] : [];
    const base = (filterOptions.assignedToUsers || []).map(u => ({
      value: String(u.id),
      label: withInactiveBadge(u.id, u.name)
    }));
    if (!selectedDepts.length) return base;
    return base.filter(u => { const e = leadsUserDeptLookup.get(u.value); return e?.deptId != null && selectedDepts.includes(e.deptId); });
  }, [filterData.department, filterOptions.assignedToUsers, leadsUserDeptLookup]);

  const cascadedLeadsCreatedByOptions = useMemo(() => {
    return (filterOptions.createdByUsers || []).map(u => ({
      value: String(u.id),
      label: withInactiveBadge(u.id, u.name)
    }));
  }, [filterOptions.createdByUsers]);

  // ✅ PERFORMANCE: Memoize filter fields for AdvancedFilterPanel
  const leadsFilterFields = useMemo(() => [
    { label: 'Lead Update Date', type: 'dateRange', fromKey: 'leadUpdateFrom', toKey: 'leadUpdateTo' },
    ...(canViewTaskUpdate ? [{ label: 'Task Update Date', type: 'dateRange', fromKey: 'taskUpdateFrom', toKey: 'taskUpdateTo' }] : []),
    { key: 'department', label: 'Department', type: 'multiselect', options: (filterOptions.departments || []).map(d => ({ value: String(d.id), label: d.name })) },
    { key: 'assignedTo', label: 'Assigned To', type: 'multiselect', options: cascadedLeadsAssignedToOptions },
    { key: 'createdBy', label: 'Created By', type: 'multiselect', options: cascadedLeadsCreatedByOptions },
    { key: 'source', label: 'Source', type: 'multiselect', options: (filterOptions.sources || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'service', label: 'Service', type: 'multiselect', options: (filterOptions.services || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'sector', label: 'Sector', type: 'select', options: (filterOptions.sectors || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'standard', label: 'Standard', type: 'select', options: (filterOptions.standards || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'stage', label: 'Stage', type: 'select', options: (filterOptions.stages || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'branchId', label: 'Branch', type: 'multiselect', options: (branches || []).map(b => ({ value: String(b.id), label: b.name || String(b.id) })) },
    { key: 'taggingIds', label: 'Tagging', type: 'multiselect', options: (taggingOptions || []).map(t => ({ value: String(t.id), label: t.name || String(t.id) })) },
    { key: 'proposalConverted', label: 'Converted to Proposal', type: 'select', options: [{ value: 'converted', label: 'Converted' }, { value: 'not_converted', label: 'Not Converted' }] },
  ], [filterOptions, branches, taggingOptions, canViewTaskUpdate, cascadedLeadsAssignedToOptions, cascadedLeadsCreatedByOptions]);

  const activeFilterCount = getActiveFilterCount(appliedFilters, leadsFilterFields);

  const leadsFilterTags = useMemo(() => {
    const tags = [];

    if (appliedFilters.period) {
      tags.push({
        key: 'period',
        label: `Period: ${String(appliedFilters.period).replace(/_/g, ' ')}`,
      });
    } else if (appliedFilters.startDate || appliedFilters.endDate) {
      tags.push({
        key: 'period',
        label: `Period: ${[appliedFilters.startDate, appliedFilters.endDate].filter(Boolean).join(' - ')}`,
      });
    }

    leadsFilterFields.forEach((field) => {
      if (field.type === 'dateRange') {
        if (appliedFilters[field.fromKey] || appliedFilters[field.toKey]) {
          tags.push({
            key: field.fromKey,
            label: `${field.label}: ${[appliedFilters[field.fromKey], appliedFilters[field.toKey]].filter(Boolean).join(' - ')}`,
          });
        }
        return;
      }
      const value = appliedFilters[field.key];
      const isEmpty = value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0);
      if (isEmpty) return;

      if (Array.isArray(value)) {
        tags.push({ key: field.key, label: `${field.label}: ${value.length} selected` });
        return;
      }

      const option = (field.options || []).find((opt) => String(opt.value) === String(value));
      tags.push({ key: field.key, label: `${field.label}: ${option ? option.label : value}` });
    });

    return tags;
  }, [appliedFilters, leadsFilterFields]);

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert.show && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={hideAlert}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          {/* <h3 className="page-subtitle m-0">Overview</h3> */}
          <h2 className="page-title">Dashboard Leads</h2>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list d-flex flex-wrap gap-2">
            {canLeads('create') && (
              <a href="#" className="btn btn-primary" onClick={(e) => {
                e.preventDefault();
                handleAddNew();
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-2">
                  <path d="M12 5l0 14"></path>
                  <path d="M5 12l14 0"></path>
                </svg>
                New Leads
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  {/* <li className="nav-item-tab" role="presentation">
                    <a
                      href="#tabs-leads-overview"
                      className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                      data-bs-toggle="tab"
                      aria-selected={activeTab === 'overview'}
                      role="tab"
                      onClick={() => handleTabChange('overview')}
                    >
                      Overview
                    </a>
                  </li> */}
                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#tabs-leads-list-data"
                      className={`nav-link ${activeTab === 'list-data' ? 'active' : ''}`}
                      data-bs-toggle="tab"
                      aria-selected={activeTab === 'list-data'}
                      tabIndex="-1"
                      role="tab"
                      onClick={() => handleTabChange('list-data')}
                    >
                      List Data
                    </a>
                  </li>
                  {/* Kanban tab hidden — causes performance lag
                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#tabs-leads-kanban"
                      className={`nav-link ${activeTab === 'kanban' ? 'active' : ''}`}
                      data-bs-toggle="tab"
                      aria-selected={activeTab === 'kanban'}
                      tabIndex="-1"
                      role="tab"
                      onClick={() => handleTabChange('kanban')}
                    >
                      Kanban
                    </a>
                  </li>
                  */}
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  {/* <div className={`tab-pane ${activeTab === 'overview' ? 'active show' : ''}`} id="tabs-leads-overview" role="tabpanel">
                    <OverviewView stats={stats} isLoading={overviewLoading} />
                  </div> */}
                  <div className={`tab-pane ${activeTab === 'list-data' ? 'active show' : ''}`} id="tabs-leads-list-data" role="tabpanel">
                    <GenericView
                      // data={dummyData}
                      // data={leadData}
                      data={filteredLeads}
                      isLoading={tableLoading || capabilitiesLoading}
                      columns={columns}
                      onEdit={canLeads('edit') ? handleEditLead : undefined}
                      onDelete={canLeads('delete') ? handleDeleteLead : undefined}
                      selectedRows={selectedLeadIds}
                      onSelectRow={(id, isSelected) => {
                        setSelectedLeadIds((prev) =>
                          isSelected ? [...prev, id] : prev.filter((rowId) => rowId !== id),
                        );
                      }}
                      onSelectAllRows={(ids) => {
                        if (Array.isArray(ids) && ids.length > 0) {
                          setSelectedLeadIds((prev) => [...new Set([...prev, ...ids])]);
                        } else {
                          const currentPageIds = filteredLeads.map((item) => item.id);
                          setSelectedLeadIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
                        }
                      }}
                      uniqueId="leads-table"
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          onSearchSubmit={handleSearchSubmit}
                          placeholder="Search Leads"
                          uniqueId="leads-list"
                        />
                      }
                      filterComponent={
                        <FilterButton offcanvasId="offcanvasLeads" activeCount={activeFilterCount} onClick={handleOpenFilter} />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={canLeads('export_data') ? handleExportExcel : undefined}
                          onExportPDF={canLeads('export_data') ? handleExportPDF : undefined}
                          uniqueId="leads-list"
                          additionalActions={
                            selectedLeadIds.length > 0 && (
                              <BulkActionsDropdown
                                selectedCount={selectedLeadIds.length}
                                onReassign={handleOpenBulkReassign}
                                onDelete={canLeads('delete') ? handleOpenBulkDelete : undefined}
                                onClearSelection={() => setSelectedLeadIds([])}
                                uniqueId="leads-bulk-actions"
                              />
                            )
                          }
                        />
                      }
                      checkboxSelection={true}
                      onRowClick={handleProjectClick}
                      emptyStateMessage="No leads found"
                      pagination={{
                        currentPage: page + 1,
                        totalRows: totalElements,
                        rowsPerPage: size,
                        onPageChange: (newPage) => {
                          const nextPage = newPage - 1;
                          setPage(nextPage);
                          persistState({ filters: appliedFilters, search: searchQuery, page: nextPage, size });
                          fetchLeads(appliedFilters, nextPage, size, sortConfig);
                        },
                        onRowsPerPageChange: (newSize) => {
                          setSize(newSize);
                          setPage(0);
                          persistState({ filters: appliedFilters, search: searchQuery, page: 0, size: newSize });
                          fetchLeads(appliedFilters, 0, newSize, sortConfig);
                        },
                      }}
                      onSortChange={(newSortConfig) => {
                        setSortConfig(newSortConfig);
                        setPage(0);
                        fetchLeads(appliedFilters, 0, size, newSortConfig);
                      }}
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title="Leads is showing a filtered view"
                          summary={`Filtered by ${activeFilterCount} ${activeFilterCount === 1 ? 'criterion' : 'criteria'}`}
                          detail={leadsFilterTags.map((tag) => tag.label).join(' · ')}
                          loading={tableLoading || capabilitiesLoading}
                          activeCount={activeFilterCount}
                          filterTags={leadsFilterTags}
                          onRemoveFilter={handleLeadsChipRemove}
                          onResetFilters={handleFilterReset}
                        />
                      }
                    />
                  </div>
                  {/* Kanban pane hidden — causes performance lag
                  <div className={`tab-pane ${activeTab === 'kanban' ? 'active show' : ''}`} id="tabs-leads-kanban" role="tabpanel">
                    {kanbanLoading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <KanbanView initialItems={leadData} onItemClick={handleProjectClick} refreshData={refreshLeadsData} />
                    )}
                  </div>
                  */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Filter Offcanvas */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasLeads"
        title="Advanced Filter"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={leadsFilterFields}
      />

      <LeadsForm
        formData={formData}
        formErrors={formErrors}
        handleInputChange={handleInputChange}
        handleTextChange={handleTextChange}
        handleFormSubmit={handleFormSubmitWithLoading}
        serviceSections={serviceSections}
        addServiceSection={addServiceSection}
        deleteServiceSection={deleteServiceSection}
        options={allOptions}
        formCascadeUsers={formCascadeUsers}
        formCascadeDepts={formCascadeDepts}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        fetchContactOptions={fetchContactOptions}
        standardsOptionsMap={standardsOptionsMap}
        fetchStandardsByServiceId={fetchStandardsByServiceId}
        onSuccessSubmit={() => {
          // Show success notification
          showAlert('success', isEditMode ? 'Lead updated successfully!' : 'Lead created successfully!');
          // Refresh the leads list after successful submission
          refreshLeadsData();
          setIsModalOpen(false);
        }}
        isEditMode={isEditMode}
        editingLead={editingLead}
        uploadedFiles={uploadedFiles}
        onDrop={onDrop}
        removeFile={removeFile}
        initializeServiceSections={initializeServiceSections}
        isIsccService={isIsccService}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onConfirm={confirmDeleteLead}
        onClose={cancelDeleteLead}
        title="Confirm Deletion"
        message={`Are you sure you want to delete lead this lead? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showBulkDeleteModal}
        onConfirm={confirmBulkDelete}
        onClose={cancelBulkDelete}
        title="Confirm Bulk Deletion"
        message={`Are you sure you want to delete ${selectedLeadIds.length} selected lead(s)? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={isBulkDeleting}
      />

      {/* Bulk Reassign Modal */}
      {showBulkReassignModal && ReactDOM.createPortal(
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
          <div
            className="modal fade show"
            style={{ display: 'block', zIndex: 1050 }}
            tabIndex="-1"
            role="dialog"
            aria-hidden="false"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Bulk Reassign {selectedLeadIds.length} Lead(s)</h5>
                  <button type="button" className="btn-close" onClick={cancelBulkReassign}></button>
                </div>
                <div className="modal-body">
                  <AppSelect
                    name="bulkReassignUserId"
                    label="Assign To"
                    value={bulkReassignUserId}
                    onChange={(name, val) => setBulkReassignUserId(val)}
                    options={options?.assignedUserOptions || []}
                    placeholder="Select an assigned user"
                    required={true}
                    valueField="value"
                    labelField="label"
                    searchField="label,email"
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={cancelBulkReassign} disabled={isBulkReassigning}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!bulkReassignUserId || isBulkReassigning}
                    onClick={confirmBulkReassign}
                  >
                    {isBulkReassigning && (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    )}
                    Confirm Reassign
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

export default Leads; 
